import Checkbox from 'app/components/checkbox'
import FullPageDialog from 'app/components/full-page-dialog'
import { NameValueColumn } from 'app/components/name-value-col'
import TextInput from 'app/components/text-input'
import { Copy2Clipboard } from 'app/modules/reports/components'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { AssetsService } from 'app/modules/shared/services'
import React, { ChangeEvent, MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { FixedSizeList } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/default-highlight'
import { atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { YaraRuleExtendedData } from '../../yara-report/types'
import { FilterValues } from './filter-values'
import DropdownSelect from 'app/components/dropdown'
import { BaseSpinner } from 'app/modules/shared/components/loader'
import YaraRuleRow from './yara-rule-row'

const YaraSearch = ({
  onApply,
  searchRules,
  filterId,
  value,
}: {
  onApply: (rules: string[], ...args: any[]) => void
  searchRules: string[]
  filterId?: string
  value?: string
}) => {
  const [isOpen, setOpen] = useState(false)
  const [allRules, setAllRules] = useState<YaraRuleExtendedData[]>([])
  const [filteredRules, setFilteredRules] = useState<YaraRuleExtendedData[]>([])
  const [selectedRules, setSelectedRules] = useState<string[]>(searchRules)
  const [previewRule, setPreviewRule] = useState<YaraRuleExtendedData | null>(null)
  const [allSelected, setAllSelected] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const debouncedSearchInput = useRef('')
  const [debounceTick, setDebounceTick] = useState(0)
  const [authorFilter, setAuthorFilter] = useState('')
  const [authors, setAuthors] = useState<string[]>([])
  const [triggerSubmit, setTriggerSubmit] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)
  const filterValues = value ? value.split(',') : []
  const [searchNamesOnly, setSearchNamesOnly] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const { _t } = useTypedTranslation()

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      debouncedSearchInput.current = value
      setDebounceTick((tick) => tick + 1) // forces a re-render so filteredBySearch recomputes
    }, 500)
  }

  const handlePreview = (rule: YaraRuleExtendedData) => {
    setPreviewRule(rule.name === previewRule?.name ? null : rule)
  }

  const handleSelectAll = () => {
    const isAllSelectedNow = !allSelected
    setSelectedRules(isAllSelectedNow ? filteredRules.map(({ name }) => name) : [])
    setAllSelected(isAllSelectedNow)
  }

  const handleSelectRule = (rule: string) => {
    const newSelectedList = selectedRules.includes(rule)
      ? selectedRules.filter((_rule) => _rule !== rule)
      : [...selectedRules, rule]

    setSelectedRules([...newSelectedList])
    setAllSelected(newSelectedList.length === filteredRules.length)

    // Enable to save cleared filter
    setEnableSubmit(!!searchRules.length)
  }

  const handleClearSelection = () => {
    setSelectedRules([])
    setAllSelected(false)
    // Enable to save cleared filter
    setEnableSubmit(!!searchRules.length)
  }

  const handleRemoveRule = (rule: string) => handleSelectRule(rule)

  const handleRemoveFromInput = (rule: string, e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    handleRemoveRule(rule)
    setTriggerSubmit(true)
  }

  const handleSubmit = () => {
    onApply(selectedRules)
  }

  // Same as array of allRules with lowercased values - index i always matches index i in allRules.
  const allRulesLower = useMemo(
    () =>
      allRules.map((rule) => ({
        name: rule.name.toLowerCase(),
        author: rule.author?.toLowerCase(),
        description: rule.description?.toLowerCase(),
        content: rule.content?.toLowerCase(),
        other_data: Object.values(rule.other_data ?? {}).map((v) => String(v).toLowerCase()),
      })),
    [allRules]
  )

  const filteredBySearch = useMemo(() => {
    if (!debouncedSearchInput.current) {
      return allRules
    }

    const searchInputLower = debouncedSearchInput.current.toLowerCase()

    return allRules.filter((_rule, index) => {
      const lowerRule = allRulesLower[index]

      if (searchNamesOnly) {
        return lowerRule.name.includes(searchInputLower)
      }

      return (
        lowerRule.name.includes(searchInputLower) ||
        lowerRule.author?.includes(searchInputLower) ||
        lowerRule.description?.includes(searchInputLower) ||
        lowerRule.content?.includes(searchInputLower) ||
        lowerRule.other_data.some((v) => v.includes(searchInputLower))
      )
    })
  }, [allRules, allRulesLower, searchNamesOnly, debounceTick])

  useEffect(() => {
    const allAuthors = [...new Set(filteredBySearch.map(({ author = '' }) => author).filter(Boolean))]
    setAuthors(allAuthors)

    const filteredByAuthor = filteredBySearch.filter((rule) => (authorFilter ? rule.author === authorFilter : true))
    setFilteredRules(filteredByAuthor)

    setAllSelected(false)
  }, [authorFilter, filteredBySearch])

  useEffect(() => {
    if (triggerSubmit) {
      setTriggerSubmit(false)
      handleSubmit()
    }
  }, [triggerSubmit])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const fetchRules = async () => {
      setIsLoading(true)
      const rules = await AssetsService.getYaraRules()
      const allAuthors = await AssetsService.getYaraAuthors()

      setAuthors(allAuthors)
      setAllRules(rules)
      setFilteredRules(rules)
      setIsLoading(false)
    }

    fetchRules()
  }, [isOpen])

  useEffect(() => setSelectedRules(searchRules), [searchRules]) // Triggered if rules removed from threat hunting's bottom filter summary

  const YaraSearchContent = () =>
    isOpen ? (
      <div className="yara-search-container">
        <div className="yara-search-filter">
          <div className="d-block flex-grow-1" style={{ flexBasis: 0 }}>
            <p className="pb-1 m-0">{_t('author')}</p>
            <DropdownSelect
              id="author"
              searchable
              value={authorFilter}
              items={[{ value: '', name: _t('all') }, ...authors.map((author) => ({ value: author, name: author }))]}
              onClick={(value) => setAuthorFilter(value)}
            />
          </div>
          <div className="d-block flex-grow-1">
            <p className="pb-1 m-0">{_t('search')}</p>
            <TextInput
              id="yara-search"
              placeholder={_t('search-rules')}
              onChange={handleSearchChange}
              prefix={<i className="ds3-icon ds3-search" />}
              clearable
            />
          </div>
          <div className="flex-grow-1 d-block m-auto">
            <p className="yara-search-cb-placeholder">{_t('search-by')}</p>
            <Checkbox
              className="d-flex align-items-center"
              label={_t('rule-names-only')}
              onChange={() => setSearchNamesOnly(!searchNamesOnly)}
              checked={searchNamesOnly}
            />
          </div>
        </div>
        <div className="yara-search-body">
          <div className="yara-rules-list">
            <div className="yara-rules-list-header">
              <Checkbox
                label={`${_t('yara-rules')} (${filteredRules.length})`}
                onChange={handleSelectAll}
                checked={allSelected}
              />
            </div>
            <div className="yara-rules-list-wrapper">
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    itemCount={filteredRules.length}
                    itemSize={41}
                    itemData={{ filteredRules, selectedRules, previewRule, handleSelectRule, handlePreview }}
                  >
                    {YaraRuleRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            </div>
          </div>
          <div className="yara-rule-content hide-mobile overflow-auto">
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center w-100">
                <BaseSpinner />
              </div>
            ) : null}
            {previewRule?.content ? (
              <div className="highlighted-code">
                <SyntaxHighlighter
                  style={atomOneLight}
                  language="C"
                  wrapLongLines
                  lineProps={{ style: { wordBreak: 'break-word' } }}
                >
                  {previewRule.content}
                </SyntaxHighlighter>
                <div className="copy-highlighted-code">
                  <Copy2Clipboard copyText={previewRule.content} />
                </div>
              </div>
            ) : null}
          </div>
          <div className="yara-rule-details hide-mobile">
            <div className="small-header mb-5 font-size-13 line-height-150">{_t('details')}</div>
            {previewRule?.author ? (
              <NameValueColumn
                name="author"
                value={previewRule?.author}
                className="mb-4"
                nameClass="font-size-13 mb-0"
              />
            ) : null}
            {previewRule?.description ? (
              <NameValueColumn
                name="description"
                value={previewRule?.description}
                className="mb-4"
                nameClass="font-size-13 mb-0"
              />
            ) : null}
            {Object.entries(previewRule?.other_data ?? {}).map(([name, value]) => {
              return (
                <NameValueColumn
                  key={name}
                  name={name}
                  value={value as string}
                  className="mb-4"
                  nameClass="font-size-13 mb-0"
                />
              )
            })}
          </div>
        </div>
      </div>
    ) : (
      <></>
    )

  return (
    <>
      <p className="text-note text-m m-0 pb-1">{_t('yara-rules')}</p>
      <div onClick={() => setOpen(true)}>
        <TextInput
          inputClassName="tags-filter-input"
          id={filterId}
          placeholder={`${_t('browse')} ${_t('yara-rules')}`}
          value={
            filterValues.length ? (
              <FilterValues values={filterValues} showAll={false} onRemove={handleRemoveFromInput} />
            ) : (
              ''
            )
          }
          postfix={<i className="ds3-icon ds3-external-link ds3-hover" />}
          cursor="pointer"
          noWrap
          readOnly
        />
      </div>
      {isOpen ? (
        <FullPageDialog
          body={YaraSearchContent()}
          title={_t('yara-rules')}
          description={_t('yara-rules-filter-description')}
          isOpen
          onClose={() => setOpen(false)}
          filteredList={selectedRules}
          contentLength={allRules.length}
          onClearSelection={handleClearSelection}
          onRemoveFilter={handleRemoveRule}
          onApply={handleSubmit}
          enableSubmit={enableSubmit}
          filterSummaryLabel={`${_t('yara-rules-selected')}:`}
          noFilterLabel={_t('no-yara-rules-selected')}
          applyButtonLabel={_t('apply')}
        />
      ) : null}
    </>
  )
}

export default YaraSearch
